import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, map, startWith, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from "@angular/material";
import * as Fuse from "fuse.js";

@Component({
    selector: 'select-many-search',
    templateUrl: 'select-many-search.component.html',
    styleUrls: ['select-search.component.less', '../forms/forms.less'],
    encapsulation: ViewEncapsulation.ShadowDom
})
export class SelectManySearchComponent implements OnInit, OnDestroy {
    description_map: { [id: string]: string } = {};

    @ViewChild('search', {static: false}) searchTextBox: ElementRef;
    selectFormControl = new FormControl();
    searchTextboxControl = new FormControl();

    emitSelected(newSelection: MatSelectChange) {
        this.setSelectedValues();
        this.selectFormControl.patchValue(this.selectedValues);

        this.valueChange.emit(this.selectedValues);
        this.selectChange.emit(this.selectedValues);
    }

    /**
     * For running a function when the selection was changed.
     */
    @Output()
    selectChange: EventEmitter<any> = new EventEmitter<any>();

    /**
     * For binding to the input value;
     */
    @Output()
    valueChange: EventEmitter<any> = new EventEmitter<any>();
    private onDestroy: Subject<void> = new Subject();

    get disabled(): boolean {
        return this.selectFormControl.disabled;
    }

    @Input()
    set disabled(state: boolean) {
        if (state) {
            this.selectFormControl.disable();
        } else {
            this.selectFormControl.enable();
        }
    }

    @Input()
    data_schema: any[] = [
        "attributes.name",
        "attributes.description",
    ];

    /**
     *
     */
    @Input()
    fill_stubs = false;

    selectedValues: any[];
    select_all: boolean = false;

    @Input()
    data: any[] = [];

    /**
     * Preselect items in the data list.
     * @param values
     */
    @Input()
    set value(values: any[]) {
        this.selectedValues = values;
        this.selectFormControl.patchValue(this.selectedValues);
    }

    /*
   Please ensure that fuse works when using the string function.
    */
    @Input()
    stringFunction: (obj: any) => string = (obj) => {
        let name_: string;
        let descrip_: string;

        if (obj.attributes && obj.attributes.name != undefined) {
            name_ = obj.attributes.name
        } else name_ = '';

        if (obj.attributes && obj.attributes.description != undefined) {
            descrip_ = obj.attributes.description
        } else return name_;

        return name_ + '-' + descrip_;
    };

    filteredOptions: any[];

    @Input() label: string = '';

    @Input() classes: string;

    search_options: any = {
        shouldSort: true,
        threshold: 0.6,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 3,
        keys: []
    };

    fuse: any;
    // account_list: Account[];
    //
    // constructor(public api: ApiService) {
    //     api.account.search().pipe(take(1)).subscribe(results => {
    //         this.account_list = results.data;
    //     });
    // }

    ngOnInit() {
        if (this.fill_stubs && this.selectedValues) {
            const data_dict: {} = {};

            this.data.forEach((item) => data_dict[item.id] = item);
            this.selectedValues = this.selectedValues.map((item: any) => data_dict[item.id]);
        } else {
            this.selectedValues = [];
        }

        this.data.forEach(item => {
            this.description_map[item.id] = this.stringFunction(item);
        });

        this.setSelectedValues();
        this.selectFormControl.patchValue(this.selectedValues);

        setTimeout(() => {
            this.search_options.keys = this.data_schema;
            this.fuse = new Fuse(this.data, this.search_options);

            /**
             * Set filter event based on value changes
             */
            this.searchTextboxControl.valueChanges.pipe(
                takeUntil(this.onDestroy),
                debounceTime(300),
                startWith<string>(''),
                map(name => this._filter(name))
            ).subscribe(values => {
                this.filteredOptions = values;

            });
        });
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }

    /**
     * Used to filter data based on search input
     */
    private _filter(name: string = ''): String[] {
        // const filterValue = name.toLowerCase();

        // let filteredList = this.data.filter(option => this.stringFunction(option).toLowerCase().indexOf(filterValue) >= 0);

        if (name === '') {
            return this.data
        }

        // Set selected values to retain the selected checkbox state
        this.setSelectedValues();
        this.selectFormControl.patchValue(this.selectedValues);
        return this.fuse.search(name);

    }

    selectAll() {
        if (this.select_all === true) {
            if (this.filteredOptions && this.filteredOptions.length > 0) {
                this.filteredOptions.forEach((e) => {
                    if (this.selectedValues.indexOf(e) == -1) {
                        this.selectedValues.push(e);
                    }
                });
            }
        } else {
            this.filteredOptions.forEach((e) => {
                let index = this.selectedValues.indexOf(e);
                if (index >= 0) {
                    this.selectedValues.splice(index, 1)
                }
            });
        }
        this.selectFormControl.patchValue(this.selectedValues);

        this.valueChange.emit(this.selectedValues);
        this.selectChange.emit(this.selectedValues);
    }

    selectionChange(event) {
        // * Remove from selected values based on uncheck - this is necessary if still using selectFromControl
        if (event.isUserInput && event.source.selected == false) {
            //@ts-ignore
            let index = this.selectedValues.indexOf(event.source.value);
            this.selectedValues.splice(index, 1)
        }
    }

    openedChange(e: boolean) {
        // Set search textbox value as empty while opening selectbox
        this.searchTextboxControl.patchValue('');
        // Focus to search textbox while clicking on selectbox
        if (e == true) {
            this.searchTextBox.nativeElement.focus();
        }
    }

    /**
     * Clearing search textbox value
     */
    clearAll(event) {
        this.select_all = false;
        this.selectedValues = [];
        this.selectFormControl.patchValue(this.selectedValues);

        this.valueChange.emit(this.selectedValues);
        this.selectChange.emit(this.selectedValues);
    }

    // TODO
    // clearSelected() {
    // }

    /**
     * Set selected values to retain the state
     */
    setSelectedValues() {
        //Note: this may need to be adjusted to accommodate matching on other attributes
        if (this.selectFormControl.value && this.selectFormControl.value.length > 0) {
            this.selectFormControl.value.forEach((e) => {
                if (!this.selectedValues.find(s => e.id === s.id)) {
                    this.selectedValues.push(e);
                }
            });
        }
    }

}

/**  Copyright 2018 Google Inc. All Rights Reserved.
 Use of this source code is governed by an MIT-style license that
 can be found in the LICENSE file at http://angular.io/license */
